import { AnimatePresence, motion, useReducedMotion } from 'motion/react'

export interface OwnershipCounterProps {
  allocated: number
  hasError?: boolean
  /**
   * MAF 26-05: `'allocated'` (default) reads "{n}% allocated" — used by Company
   * Structure. `'remaining'` reads "Remaining: {100 − n}%" for the Processing
   * Breakdown. Colour states are identical: red at 0 (nothing allocated yet),
   * orange while partial, green at exactly 100, red (exceeds) above.
   */
  mode?: 'allocated' | 'remaining'
}

function getCounterState(allocated: number, hasError: boolean) {
  if (allocated === 100) return 'success' // exactly 100% → green, ready to proceed
  if (allocated > 100) return 'warning'   // over-allocated → red ("exceeds 100%")
  if (allocated === 0) return 'empty'     // nothing allocated yet → red, can't proceed
  if (hasError) return 'error'            // failed validation → red
  return 'normal'                         // partial (1–99%) → orange, still in progress
}

export function OwnershipCounter({ allocated, hasError = false, mode = 'allocated' }: OwnershipCounterProps) {
  const prefersReducedMotion = useReducedMotion()
  const isRemaining = mode === 'remaining'
  const state = getCounterState(allocated, hasError)
  // In remaining mode the visible figure counts down from 100 to 0.
  const displayValue = isRemaining ? 100 - allocated : allocated

  const styles: Record<string, React.CSSProperties> = {
    // Partial progress (1–99%): orange — allocation started but not yet complete.
    normal: {
      background: 'var(--counter-warning-bg)',
      color: 'var(--counter-warning-text)',
      border: '1px solid var(--counter-warning-border)',
    },
    // Over-allocated (>100%): red — exceeds the limit and blocks completion.
    warning: {
      background: 'var(--counter-error-bg)',
      color: 'var(--counter-error-text)',
      border: '1px solid var(--counter-error-border)',
    },
    success: {
      background: 'var(--counter-success-bg)',
      color: 'var(--counter-success-text)',
      border: '1px solid var(--counter-success-border)',
    },
    // Nothing allocated yet (0%): red, but keeps the plain "% allocated" label.
    empty: {
      background: 'var(--counter-error-bg)',
      color: 'var(--counter-error-text)',
      border: '1px solid var(--counter-error-border)',
    },
    error: {
      background: 'var(--counter-error-bg)',
      color: 'var(--counter-error-text)',
      border: '1px solid var(--counter-error-border)',
    },
  }

  const containerStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 'var(--spacing-1)',
    padding: 'var(--spacing-2) var(--spacing-4)',
    borderRadius: 'var(--radius-full)',
    fontFamily: 'var(--font-body)',
    fontSize: 'var(--text-body-sm)',
    fontWeight: 'var(--weight-semibold)',
    lineHeight: 'var(--leading-normal)',
    ...styles[state],
    transition: 'background 250ms ease-in-out, color 250ms ease-in-out, border-color 250ms ease-in-out',
  }

  const liveText = isRemaining
    ? state === 'warning'
      ? `Regional percentages total ${allocated}%, which exceeds 100%. Please adjust before continuing.`
      : state === 'error'
      ? `Regional percentages total ${allocated}%. They must equal 100% to continue.`
      : state === 'success'
      ? 'All regions allocated. 100% total. Complete.'
      : `${100 - allocated}% remaining to allocate.`
    : state === 'warning'
      ? `Warning: ownership total is ${allocated}%, which exceeds 100%. Please adjust before continuing.`
      : state === 'error'
      ? `Error: ownership total is ${allocated}%. It must equal 100% to continue.`
      : state === 'success'
      ? 'Ownership total is 100%. Complete.'
      : `${allocated}% allocated`

  // Label after the number differs by mode; colour states are shared.
  const labelAfter = isRemaining
    ? state === 'success'
      ? '% ✓'
      : '%'
    : state === 'success'
      ? '% allocated ✓'
      : state === 'warning'
      ? '% allocated (exceeds 100%)'
      : state === 'error'
      ? '% allocated (must equal 100%)'
      : '% allocated'

  return (
    <div
      style={containerStyle}
      role="status"
      aria-live={state === 'error' || state === 'warning' ? 'assertive' : 'polite'}
      aria-label={liveText}
    >
      {/* Prefix label (remaining mode reads "Remaining: {n}%") */}
      {isRemaining && <span>Remaining:</span>}

      {/* Animated digit */}
      {prefersReducedMotion ? (
        <span style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--text-body-md)', fontWeight: 'var(--weight-semibold)' }}>
          {displayValue}
        </span>
      ) : (
        <AnimatePresence mode="wait">
          <motion.span
            key={displayValue}
            initial={{ y: '-100%', opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: '100%', opacity: 0 }}
            transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            style={{
              display: 'inline-block',
              fontFamily: 'var(--font-display)',
              fontSize: 'var(--text-body-md)',
              fontWeight: 'var(--weight-semibold)',
            }}
          >
            {displayValue}
          </motion.span>
        </AnimatePresence>
      )}

      {/* Label text after the number */}
      <span>{labelAfter}</span>
    </div>
  )
}
